import { t } from "@/i18n-js/instance";
import type { Event, FormScheduleValues } from "@circle-react/types";
import { Icon } from "@circle-react-shared/Icon";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { Typography } from "@circle-react-uikit/Typography";
import { useCalculateSchedule } from "../../../hooks/useCalculateSchedule";
import { RECURRING_EDIT_MODE } from "../../../utils";
import {
  formatCurrentFormStartsAt,
  formatEventStartsAt,
} from "../../../utils/formatCurrentSchedule";
import { formatRecurringSchedule } from "../../../utils/formatRecurringSchedule";
import { AttendeeExperienceSection } from "../AttendeeExperienceSection";

type RecurringEditMode =
  (typeof RECURRING_EDIT_MODE)[keyof typeof RECURRING_EDIT_MODE];

interface ScheduleUpdateConfirmationModalProps {
  editMode?: RecurringEditMode;
  event: Event;
  formScheduleValues: FormScheduleValues;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const getFormattedOriginalSchedule = (
  event: Event,
  isCurrentEditMode: boolean,
): string => {
  if (isCurrentEditMode) {
    return formatEventStartsAt(event);
  }

  return formatRecurringSchedule({
    frequency: event.recurring_setting_attributes?.frequency || "",
    startsAt: event.event_setting_attributes?.starts_at || "",
    endsAt: event.recurring_setting_attributes?.ends_at,
    occurrences: event.recurring_setting_attributes?.occurrences,
  });
};

const getFormattedNewSchedule = (
  data: any,
  formScheduleValues: FormScheduleValues,
  isCurrentEditMode: boolean,
): string => {
  if (isCurrentEditMode) {
    return formatCurrentFormStartsAt(formScheduleValues);
  }

  if (!data?.schedule) return "";

  return formatRecurringSchedule({
    frequency: data.schedule.frequency,
    startsAt: data.schedule.starts_at,
    endsAt: data.schedule.ends_at,
    occurrences: data.schedule.occurrences,
  });
};

const shouldShowLoadingState = (
  isLoadingNewSchedule: boolean,
  isCurrentEditMode: boolean,
): boolean => isLoadingNewSchedule && !isCurrentEditMode;

const shouldShowErrorState = (
  error: any,
  isCurrentEditMode: boolean,
): boolean => !!error && !isCurrentEditMode;

export const ScheduleUpdateConfirmationModal = ({
  event,
  isOpen,
  onCancel,
  onConfirm,
  formScheduleValues,
  editMode,
}: ScheduleUpdateConfirmationModalProps) => {
  const isCurrentEditMode = editMode === RECURRING_EDIT_MODE.CURRENT;

  const {
    isLoading: isLoadingNewSchedule,
    data,
    error,
  } = useCalculateSchedule(
    isOpen && event && formScheduleValues && !isCurrentEditMode
      ? { event, formScheduleValues }
      : undefined,
  );

  const formattedEventName = event?.name;
  const formattedOriginalSchedule = getFormattedOriginalSchedule(
    event,
    isCurrentEditMode,
  );
  const formattedNewSchedule = getFormattedNewSchedule(
    data,
    formScheduleValues,
    isCurrentEditMode,
  );
  const isLoadingState = shouldShowLoadingState(
    isLoadingNewSchedule,
    isCurrentEditMode,
  );
  const isErrorState = shouldShowErrorState(error, isCurrentEditMode);

  return (
    <ConfirmationModal
      title={t("events.edit_modal.schedule_update_modal.title")}
      isOpen={isOpen}
      confirmText={t("events.edit_modal.schedule_update_modal.update_schedule")}
      cancelText={t("events.edit_modal.schedule_update_modal.back")}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={onConfirm}
      size="lg"
      disabled={!!error || isLoadingState}
    >
      <div className="space-y-6">
        {/* Schedule Details Section */}
        <div className="flex flex-col rounded-xl border p-4 pt-3 shadow-lg">
          <div className="border-primary border-b pb-2">
            <div className="flex items-center gap-2 pb-2">
              <Typography.LabelMd weight="semibold" textTransform="capitalize">
                {formattedEventName}
              </Typography.LabelMd>
              <Typography.BodyMd color="text-secondary">
                {t("events.edit_modal.schedule_update_modal.updated_schedule")}
              </Typography.BodyMd>
            </div>
            {!isCurrentEditMode && (
              <div className="pb-2">
                <Typography.BodySm color="text-secondary">
                  {t(
                    "events.edit_modal.schedule_update_modal.series_update_notice",
                  )}
                </Typography.BodySm>
              </div>
            )}
          </div>

          {/* Original Series */}
          <div className="border-primary relative flex flex-col gap-2 border-b pb-6 pt-4">
            <Typography.LabelSm weight="semibold" color="text-secondary">
              {isCurrentEditMode
                ? t(
                    "events.edit_modal.schedule_update_modal.original_date_time",
                  )
                : t("events.edit_modal.schedule_update_modal.original_series")}
            </Typography.LabelSm>
            <div className="flex items-center gap-2">
              <Icon
                type="16-calendar-recurring"
                size={16}
                className="text-default !w-4"
              />
              <Typography.BodySm color="text-secondary">
                {formattedOriginalSchedule}
              </Typography.BodySm>
            </div>
            {/* Arrow */}
            <div className="bg-primary absolute left-1/2 top-full flex -translate-x-1/2 -translate-y-1/2 justify-center rounded-full border p-1">
              <Icon type="16-arrow-down" size={16} className="!w-4" />
            </div>
          </div>

          {/* New Series */}
          <div className="border-primary relative flex flex-col gap-2 pb-2 pt-4">
            <Typography.LabelSm weight="semibold" color="text-secondary">
              {isCurrentEditMode
                ? t("events.edit_modal.schedule_update_modal.new_date_time")
                : t("events.edit_modal.schedule_update_modal.new_series")}
            </Typography.LabelSm>
            <div className="flex items-center gap-2">
              <Icon
                type="16-calendar-recurring"
                size={16}
                className="text-default !w-4"
              />
              {isLoadingState ? (
                <Icon type="loader" size={16} className="animate-spin" />
              ) : isErrorState ? (
                <Typography.BodySm color="text-v2-danger">
                  {error instanceof Error
                    ? error.message
                    : t(
                        "events.edit_modal.schedule_update_modal.calculation_failed",
                      )}
                </Typography.BodySm>
              ) : (
                <Typography.BodySm color="text-secondary">
                  {formattedNewSchedule}
                </Typography.BodySm>
              )}
            </div>
          </div>
        </div>

        {/* What attendees will experience Section */}
        {!isCurrentEditMode && <AttendeeExperienceSection />}
      </div>
    </ConfirmationModal>
  );
};
